import Color from 'color';
import {css} from 'styled-components';

import type {Props} from './types';

import {getColor, getRem, ms, square} from '../../styles';

const base = css<{scheme: Props['scheme']}>`
  .react-datepicker {
    display: flex;
    background-color: ${getColor('background1')};
    flex-direction: column;
    font-family: inherit;
    gap: ${ms(8)};
    border: none;
    width: 100%;
  }

  &&
    .react-datepicker-popper[data-placement^='top']
    .react-datepicker__triangle {
    border-top-color: ${getColor('background1')};
  }

  &&
    .react-datepicker-popper[data-placement^='bottom']
    .react-datepicker__triangle::before {
    border-bottom-color: ${p =>
      getColor(p.scheme === 'secondary' ? 'global2' : 'global1')(p)};
  }

  &&
    .react-datepicker-popper[data-placement^='bottom']
    .react-datepicker__triangle {
    border-bottom-color: ${getColor('background1')};
  }

  &&
    .react-datepicker-popper[data-placement^='top']
    .react-datepicker__triangle::before {
    border-top-color: ${p =>
      getColor(p.scheme === 'secondary' ? 'global2' : 'global1')(p)};
  }

  && .react-datepicker__navigation-icon--previous::before {
    border-color: ${getColor('text3')};
    border-width: 2px 2px 0 0;
    top: ${ms(12)};
  }

  && .react-datepicker__navigation-icon--next::before {
    border-color: ${getColor('text3')};
    border-width: 2px 2px 0 0;
    top: ${ms(12)};
  }

  && .react-datepicker__header {
    background-color: transparent;
    border: none;
  }

  && .react-datepicker__day-names {
    display: flex;
    justify-content: center;
    gap: ${ms(10)};
  }

  && .react-datepicker__day-name {
    flex-basis: ${ms(40)};
    flex-shrink: 1;
    line-height: ${getRem(40)};
    font-size: ${getRem(12)};
    color: ${getColor('text1')};
  }

  && .react-datepicker__month-container {
    flex: 1;
    container-type: inline-size;

    @container (max-width: 300px) {
      .react-datepicker__day-names {
        gap: 0;
      }

      .react-datepicker__week {
        gap: 0;
      }

      .react-datepicker__day {
        font-size: ${getRem(12)};
      }
    }
  }

  && .react-datepicker__month {
    display: flex;
    flex-direction: column;
    margin: 0;
    gap: ${ms(10)};
  }

  && .react-datepicker__week {
    display: flex;
    justify-content: center;
    gap: ${ms(10)};
  }

  && .react-datepicker__current-month {
    color: ${getColor('text1')};
  }

  && .react-datepicker__day {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    aspect-ratio: 1 / 1;
    border-radius: 50%;
    color: ${getColor('text1')};
    flex-basis: ${ms(40)};
    flex-shrink: 1;
    font-size: ${getRem(16)};
  }

  && .react-datepicker__day--outside-month {
    color: ${getColor('text3')};
  }

  && .react-datepicker__day--today {
    background-color: transparent;
    color: ${p =>
      getColor(p.scheme === 'secondary' ? 'global2' : 'global1')(p)};
  }

  && .react-datepicker__day--selected,
  && .react-datepicker__day--in-range,
  && .react-datepicker__day--in-range:hover {
    background-color: ${p =>
      getColor(p.scheme === 'secondary' ? 'global2' : 'global1')(p)} !important;
    color: ${getColor('text.white')} !important;
  }

  && .react-datepicker__day--in-selecting-range {
    background-color: ${p =>
      Color(getColor(p.scheme === 'secondary' ? 'global2' : 'global1')(p))
        .alpha(0.5)
        .toString()} !important;
  }

  && .react-datepicker__day--disabled {
    color: ${p => Color(getColor('text3')(p)).alpha(0.5).toString()};
    cursor: not-allowed;
  }

  && .react-datepicker__day--selected.react-datepicker__day--selected:hover {
    background-color: ${p =>
      getColor(p.scheme === 'secondary' ? 'global2' : 'global1')(p)};
    border-radius: 50%;
  }

  && .react-datepicker__day--selected.react-datepicker__day--selected:focus {
    box-shadow: none;
  }

  && .react-datepicker__day--keyboard-selected {
    background-color: ${getColor('background1')};
  }

  &&
    .react-datepicker__day--keyboard-selected.react-datepicker__day--keyboard-selected:focus {
    box-shadow: none;
  }

  && .react-datepicker__day:hover {
    background-color: ${getColor('background2')};
    border-radius: 50%;
  }

  && .react-datepicker__day:focus {
    outline: none;
    box-shadow: 0 0 4px 1px
      ${p => getColor(p.scheme === 'secondary' ? 'global2' : 'global1')(p)};
  }

  && .react-datepicker__navigation.react-datepicker__navigation--previous:focus,
  && .react-datepicker__navigation.react-datepicker__navigation--next:focus {
    outline: none;
  }

  && .react-datepicker__input-time-container {
    display: flex;
    justify-content: center;
    width: 100%;
  }

  && .react-datepicker__input-time-container .react-datepicker-time__caption {
    color: ${getColor('text.black')};
    font-size: ${getRem(16)};
  }

  &&
    .react-datepicker__input-time-container
    .react-datepicker-time__input-container
    .react-datepicker-time__input
    input[type='time'] {
    color: ${getColor('text.black')};
    font-size: ${getRem(16)};
    background: ${getColor('background.white')};
    border: 1px solid ${p => getColor('text.black')(p)};
    border-radius: ${ms(2)};
  }
`;

export const web = css<{scheme: Props['scheme']}>`
  box-sizing: border-box;
  ${base}
`;

export const headerButton = css`
  ${square(35)}
  background-color: transparent;
  border: none;
  outline: none;
  cursor: pointer;

  :disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  svg {
    ${square(24)}
  }
`;

export const headerContainer = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: transparent;
  width: 100%;
  max-width: ${ms(380)};
  margin-inline: auto;
`;
